import {
  type MutableRefObject,
  forwardRef,
  useEffect,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import classNames from 'classnames/bind';
import { optionalProp } from '../core';
import { EVENT_KEY, MLPVersion, Theme } from '../types/enums';
import { Breakpoints, useBreakpoint } from '../Breakpoint/BreakpointProvider';
import Button, { ButtonSize, ButtonStyles, ButtonTypes, ButtonWidth } from '../Button/Button';
import Drawer from '../Drawer/Drawer';
import { FAIcon } from '../Icons/Icons';
import Tooltip, { TooltipPreference } from '../Tooltip/Tooltip';
import { useClickOutsideDetector } from '../hooks';
import { useTranslation } from '../Translation/TranslationProvider';
import FloatLabelInput from '../Input/FloatLabelInput/FloatLabelInput';
import type { InputValidations } from '../Input/Input';
import Calendar, { CalendarYearDropdownType, CalendarType } from './Calendar/Calendar';
import { timeInFormat } from './Calendar/CalendarHelper';
import styles from './DatePicker.module.scss';
import { assignIfExists, getDateTimeText } from './DatePickerHelper';
import TimePicker from './TimePicker/TimePicker';
import { CalendarWidth, DateFormat, DatePickerType, DatePickerValidataionStatus } from './types/enums';
import {
  type RefType,
  type TimePickerProps,
  type ComponentUserEvent,
  type DatePickerProps,
  PLACEHOLDER_TEXT,
} from './types/propTypes';

const cx = classNames.bind(styles);

const RenderComponent = ({
  mlpVersion,
  ariaLabelValue,
  disabled,
  isInputFocused,
  componentUserEvent,
  status,
  placeholder,
  value,
  buttonClassname,
  inputLabel,
  required,
  labelInfo,
  labelInfoPreference,
  inputContainerHeight,
  compact,
}: {
  mlpVersion: MLPVersion;
  ariaLabelValue: string;
  disabled: boolean;
  isInputFocused: boolean;
  componentUserEvent: ComponentUserEvent;
  status: DatePickerValidataionStatus;
  placeholder: string;
  value: string;
  buttonClassname: string;
  inputLabel?: string;
  required?: boolean;
  labelInfo?: string;
  labelInfoPreference?: TooltipPreference;
  inputContainerHeight?: string;
  compact?: boolean;
}) => {
  const currentStatus = isInputFocused ? {} : { status: status as unknown as InputValidations };
  return mlpVersion === MLPVersion.TWO ? (
    <FloatLabelInput
      name="datepicker-mlpversion-2"
      aria-label={ariaLabelValue}
      disabled={disabled}
      {...optionalProp('label', inputLabel)}
      isInputFocused={isInputFocused}
      {...componentUserEvent}
      inputIconOnClick={componentUserEvent.onClick}
      {...currentStatus}
      placeholder={placeholder}
      value={value}
      inputIcon={{ iconName: 'calendar' }}
    />
  ) : (
    <>
      {inputLabel && (
        <p className={styles['input-label']}>
          {inputLabel}
          {required && (
            <span className={styles['required-indicator']} data-testid="date-picker-required">
              *
            </span>
          )}
          {labelInfo && (
            <Tooltip
              message={labelInfo}
              {...optionalProp('preference', labelInfoPreference)}
              className={styles['date-picker-tool-tip']}
            />
          )}
        </p>
      )}
      <button
        type="button"
        className={buttonClassname}
        style={inputContainerHeight ? { height: inputContainerHeight } : {}}
        data-testid="input-container"
        {...componentUserEvent}
        tabIndex={0}
        aria-label={ariaLabelValue}
      >
        <span className={styles['placeholder-container']} data-value={value || PLACEHOLDER_TEXT}>
          {value || placeholder}
        </span>
        <FAIcon iconName="calendar" className={`${styles['calendar-icon']} ${compact ? styles['compact'] : ''}`} />
      </button>
    </>
  );
};
const DatePicker = forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      onChange,
      inputLabel,
      labelInfo = '',
      labelInfoPreference = TooltipPreference.RIGHT,
      placeholder = '',
      cancelLabel,
      scheduleLabel,
      onCalendarToggle,
      onSelect,
      isWithinModal = false,
      displayText,
      description,
      calendarHeading,
      calendarFooter,
      inputContainerHeight,
      ariaLabel,
      disabled = false,
      isWithinDrawer = false,
      calendarWidth = CalendarWidth.DEFAULT,
      onClose,
      type = DatePickerType.DROP_DOWN,
      status = DatePickerValidataionStatus.DEFAULT,
      value,
      valueTo,
      startTimeValue,
      endTimeValue,
      is24HoursFormat = true,
      required = false,
      mlpVersion = MLPVersion.ONE,
      dateFormat = DateFormat.DDMMYYYY,
      compact = false,

      ...restProps
    },
    ref,
  ) => {
    // State
    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedDate, setSelectedDate] = useState(value);
    const [selectedEndDate, setSelectedEndDate] = useState(valueTo);

    const [selectedStartTime, setSelectedStartTime] = useState<string | undefined>();
    const [selectedEndTime, setSelectedEndTime] = useState<string | undefined>();
    const [displayValue, setDisplayValue] = useState(
      getDateTimeText(
        dateFormat,
        value,
        valueTo,
        startTimeValue,
        endTimeValue,
        restProps.showTime,
        restProps.selectRange,
      ),
    );

    const t = useTranslation();
    const ariaLabelValue = ariaLabel || t('nextgen.Datepicker.ariaLabel');
    const isMonthYearType = restProps?.calendarType === CalendarType.ONLY_MONTH_YEAR_SELECTOR;

    // update value if props updated after render
    useEffect(() => {
      setSelectedDate(value);
      const currentStartTime = timeInFormat(is24HoursFormat || false, startTimeValue);
      if (restProps.showTime) {
        setSelectedStartTime(currentStartTime);
      }
      let currentEndTime = endTimeValue;
      if (restProps.selectRange) {
        setSelectedEndDate(valueTo);
        if (restProps.showTime) {
          currentEndTime = timeInFormat(is24HoursFormat || false, endTimeValue);
          setSelectedEndTime(currentEndTime);
        }
      }
      setDisplayValue(
        getDateTimeText(
          isMonthYearType ? DateFormat.YYYYMM : dateFormat,
          value,
          valueTo,
          currentStartTime,
          currentEndTime,
          restProps.showTime,
          restProps.selectRange,
        ),
      );
    }, [value, valueTo, startTimeValue, endTimeValue, dateFormat]);
    // Datepicker ref to identify user interactions.
    const datePickerRef = useRef() as MutableRefObject<HTMLDivElement>;
    const calendarRef = useRef<RefType>(null);
    const viewPortType = useBreakpoint();
    const isSmallDevice = () => {
      const breakpointsArr: string[] = [Breakpoints.XS, Breakpoints.SM];
      return breakpointsArr.includes(viewPortType);
    };

    useEffect(() => {
      // converts time format once page loaded
      if (type !== DatePickerType.MODAL || !is24HoursFormat) {
        const currentStartTime = timeInFormat(false, startTimeValue);
        const currentEndTime = timeInFormat(false, endTimeValue);
        setSelectedStartTime(currentStartTime);
        setSelectedEndTime(currentEndTime);
        setDisplayValue(
          getDateTimeText(
            isMonthYearType ? DateFormat.YYYYMM : dateFormat,
            value,
            valueTo,
            currentStartTime,
            currentEndTime,
            restProps.showTime,
            restProps.selectRange,
          ),
        );
      }
    }, []);
    const outsideClickAction = () => {
      if (type === DatePickerType.DROP_DOWN) {
        setShowCalendar(false);
      }
    };
    useClickOutsideDetector(datePickerRef, outsideClickAction);
    function handleDateTimeChange(
      currentSelectedDate?: Date,
      currentSelectedEndDate?: Date,
      currentSelectedStartTime?: string,
      currentSelectedEndTime?: string,
      event?: ReactKeyboardEvent | ReactMouseEvent | ReactFocusEvent,
    ) {
      if (restProps.selectRange) {
        setSelectedDate(currentSelectedDate);
        setSelectedEndDate(currentSelectedEndDate);
        setSelectedStartTime(currentSelectedStartTime);
        setSelectedEndTime(currentSelectedEndTime);
      } else {
        assignIfExists(currentSelectedDate, setSelectedDate);
        assignIfExists(currentSelectedEndDate, setSelectedEndDate);
        assignIfExists(currentSelectedStartTime, setSelectedStartTime);
        assignIfExists(currentSelectedEndTime, setSelectedEndTime);
      }
      if (type === DatePickerType.MODAL) {
        setSelectedStartTime(currentSelectedStartTime);
        setSelectedEndTime(currentSelectedEndTime);
        setDisplayValue(
          getDateTimeText(
            isMonthYearType ? DateFormat.YYYYMM : dateFormat,
            currentSelectedDate,
            currentSelectedEndDate,
            currentSelectedStartTime,
            currentSelectedEndTime,
            restProps.showTime,
            restProps.selectRange,
          ),
        );
        onChange?.(
          currentSelectedDate,
          currentSelectedEndDate,
          currentSelectedStartTime,
          currentSelectedEndTime,
          event,
        );
      }
      if (isMonthYearType) {
        onChange?.(
          currentSelectedDate,
          currentSelectedEndDate,
          currentSelectedStartTime,
          currentSelectedEndTime,
          event,
        );
        setShowCalendar(false);
      }
      onSelect?.(event, currentSelectedDate);
    }
    function handleCalendarVisibility() {
      const tempState = !showCalendar;
      setShowCalendar(tempState);
      if (tempState) {
        onClose?.();
      }
    }
    function handleCancelBtnClick(event: ReactMouseEvent) {
      handleDateTimeChange(undefined, undefined, undefined, undefined, event);
      handleCalendarVisibility();
    }
    function handleScheduleClick(event: ReactKeyboardEvent | ReactMouseEvent | ReactFocusEvent) {
      setDisplayValue(
        getDateTimeText(
          isMonthYearType ? DateFormat.YYYYMM : dateFormat,
          selectedDate,
          selectedEndDate,
          selectedStartTime,
          selectedEndTime,
          restProps.showTime,
          restProps.selectRange,
        ),
      );
      onChange?.(selectedDate, selectedEndDate, selectedStartTime, selectedEndTime, event);
      handleCalendarVisibility();
    }

    const actionButtons = compact ? null : (
      <div className={styles['action-button-container']}>
        <span className={styles['action-button']}>
          <Button
            title={cancelLabel}
            type={ButtonTypes.BUTTON}
            theme={Theme.BLACK}
            size={ButtonSize.SMALL}
            buttonStyle={ButtonStyles.OUTLINE}
            onClick={handleCancelBtnClick}
            buttonWidth={ButtonWidth.FULL_WIDTH}
          />
        </span>
        <span className={`${styles['action-button']} ${styles['primary-action-button']}`}>
          <Button
            title={scheduleLabel}
            type={ButtonTypes.BUTTON}
            theme={Theme.BLACK}
            size={ButtonSize.SMALL}
            buttonStyle={ButtonStyles.SOLID}
            onClick={handleScheduleClick}
            data-testid="schedule-btn"
            buttonWidth={ButtonWidth.FULL_WIDTH}
          />
        </span>
      </div>
    );
    const closeIconComponent = onClose ? (
      <FAIcon
        iconName="xmark"
        className={styles['icon-close']}
        onClick={handleCalendarVisibility}
        data-testid="heading-close-icon"
      />
    ) : null;
    const descriptionContent =
      compact || !calendarHeading ? null : (
        <div className={styles['calendar-header']}>
          {!isSmallDevice() ? (
            <h3 className={styles['calendar-header-title']}>
              {calendarHeading}
              {closeIconComponent}
            </h3>
          ) : null}
          {description ? (
            <p className={`${styles['description-container']} ${styles['datePicker-modal-description-container']}`}>
              {description}
            </p>
          ) : null}
        </div>
      );

    const calendarComponent = (
      <div className={styles['calendar-body']}>
        <Calendar
          {...restProps}
          onChange={handleDateTimeChange}
          ref={calendarRef}
          forwardedRef={ref}
          cancelLabel={cancelLabel}
          scheduleLabel={scheduleLabel}
          type={isSmallDevice() ? DatePickerType.DROP_DOWN : type}
          onClose={handleCalendarVisibility}
          is24HoursFormat={is24HoursFormat}
          {...optionalProp('calendarHeading', calendarHeading)}
          {...optionalProp('value', selectedDate)}
          {...optionalProp('valueTo', selectedEndDate)}
          {...optionalProp('startTimeValue', selectedStartTime)}
          {...optionalProp('endTimeValue', selectedEndTime)}
        />
      </div>
    );

    // Calendar CSS Classes
    const calendarDefaultCls = `${styles['menu-container']} ${styles[calendarWidth]} ${
      isWithinDrawer ? styles['within-drawer'] : ''
    } ${compact ? styles['compact'] : ''}`;
    const calendarModalCls = styles['calendar-modal-container'];
    const footerContent = calendarFooter ? <div className={styles['calendar-footer']}>{calendarFooter}</div> : null;
    const fallbackContent = showCalendar ? <span className={calendarModalCls}>{calendarComponent}</span> : null;
    const defaultContent =
      isSmallDevice() || type === DatePickerType.DROP_DOWN ? (
        <div className={calendarDefaultCls}>
          {descriptionContent}
          {calendarComponent}
          {actionButtons}
        </div>
      ) : (
        fallbackContent
      );

    const drawerContent = showCalendar ? (
      <Drawer
        header={<span>{inputLabel || calendarHeading}</span>}
        footer={<>{actionButtons}</>}
        closeIcon
        onClose={() => setShowCalendar(false)}
      >
        {descriptionContent}
        <div className={styles['calendar-wrapper']}>{calendarComponent}</div>
        {footerContent}
      </Drawer>
    ) : null;
    const ContainerClassname = cx({
      'date-picker-container-within-modal': isWithinModal,
      'date-picker-container': !isWithinModal,
      'calendar-state-open': showCalendar,
    });
    const ButtonClassname = cx(
      'input-container',
      {
        'input-container-active': showCalendar,
      },
      [`datepicker-${status}`],
      { 'datepicker-input-disabled': disabled },
      {
        compact: compact,
      },
    );
    const componentUserEvent = {
      onClick: (e: ReactMouseEvent<HTMLInputElement | HTMLButtonElement, MouseEvent>) => {
        onCalendarToggle?.(e);
        handleCalendarVisibility();
      },
      onKeyDown: (e: ReactKeyboardEvent<HTMLInputElement | HTMLButtonElement>) => {
        if (e.key === EVENT_KEY.ENTER) {
          onCalendarToggle?.(e);
          handleCalendarVisibility();
        }
      },
    };
    return (
      <>
        <div
          className={ContainerClassname}
          data-testid="date-picker-container"
          id="nextgen-datepicker-component"
          ref={datePickerRef}
        >
          {RenderComponent({
            mlpVersion,
            ariaLabelValue,
            disabled,
            isInputFocused: showCalendar,
            componentUserEvent,
            status,
            placeholder,
            labelInfo,
            labelInfoPreference,
            buttonClassname: ButtonClassname,
            value: displayText || displayValue,
            required,
            ...{ ...optionalProp('inputLabel', inputLabel) },
            ...{ ...optionalProp('inputContainerHeight', inputContainerHeight) },
            compact,
          })}
          {isSmallDevice() ? drawerContent : defaultContent}
        </div>
      </>
    );
  },
);
DatePicker.displayName = 'DatePicker';
export {
  Calendar,
  CalendarYearDropdownType,
  CalendarWidth,
  DatePickerType,
  CalendarType,
  TimePicker,
  DatePickerValidataionStatus,
  DateFormat,
};
export type { TimePickerProps };
export default DatePicker;
